import { Formik, FormikHelpers } from 'formik';
import _ from 'lodash';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useState } from 'react';
import { usePreventNavigation } from 'shared/hooks';

import { DefaultDropzone } from 'ud-form/components/dropzoneV2';
import UDFormInput from 'ud-form/components/input';
import UDFormLabel from 'ud-form/components/label';
import UDButton from 'ud-ui/components/button';
import * as yup from 'yup';
import { AnnouncedTeam } from '../../../domain/interfaces/announced-team';
import { Team } from '../../../domain/interfaces/team';

type FormValues = {
  name: string;
  shortName: string;
  logo?: (File | string)[];
};

export type SubmitTeamForm = ({ values, formikHelpers }: { values: FormValues; formikHelpers: FormikHelpers<FormValues> }) => void;

type EditTeamFormProps = {
  onSubmit: SubmitTeamForm;
  initialValues?: Team | AnnouncedTeam;
  isLoading?: boolean;
};

const validateScheme = yup.object().shape({
  name: yup.string().required('Название команды обязательно'),
  shortName: yup.string().required('Короткое название обязательно').max(255, 'Максимальная длина короткого названия 255 символов'),
  logo: yup.mixed().nullable().optional(),
});

const TeamForm = (props: EditTeamFormProps) => {
  const { initialValues: initialTeam, onSubmit, isLoading } = props;

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [leavingConfirmed, setLeavingConfirmed] = useState(false);

  const { backHandler, PreventNavigationModal } = usePreventNavigation({
    shouldPrevent: isFormDirty && !leavingConfirmed,
    onConfirm: () => {
      setLeavingConfirmed(true);
      setIsFormDirty(false);
    },
    defaultPath: Pages.TEAMS.INDEX,
  });

  const initialValues: FormValues = {
    name: initialTeam?.name ?? '',
    shortName: initialTeam?.shortName ?? '',
    logo: initialTeam?.logo ? [initialTeam.logo.url] : [],
  };

  return (
    <Formik
      validateOnMount
      initialValues={initialValues}
      validationSchema={validateScheme}
      onSubmit={(values, formikHelpers) => {
        onSubmit({ values, formikHelpers });
        formikHelpers.resetForm({ values }); // делаем текущие значения базой
        setIsFormDirty(false);
      }}
      validate={(values) => {
        const dirty = !_.isEqual(values, initialValues);
        if (dirty !== isFormDirty && !leavingConfirmed) {
          setIsFormDirty(dirty);
        }
        return {};
      }}
    >
      {(formProps) => {
        return (
          <form className="d-flex flex-column" onSubmit={formProps.handleSubmit}>
            <div className="row align-items-start gx-4">
              <div className={'col-6 flex flex-col'}>
                <UDFormInput name="name" label="Название команды" placeholder="Введите название" />
                <UDFormInput
                  name="shortName"
                  label="Короткое название (аббревиатура)"
                  placeholder="Введите короткое название (напр. MUN)"
                  containerProps={{ className: 'mt-4' }}
                  maxLength={255}
                />
              </div>
              <div className="col-6">
                <UDFormLabel className="mb-2">Логотип команды</UDFormLabel>
                <DefaultDropzone name="logo" className="p-6" />
              </div>
            </div>

            <div className="d-flex justify-content-start col-6 mb-2 mt-4 pr-6">
              <UDButton
                type="submit"
                variant="primary"
                className="w-50 mr-4"
                disabled={isLoading || !formProps.dirty}
                loading={isLoading || formProps.isSubmitting}
              >
                Сохранить
              </UDButton>

              <UDButton
                type="button"
                variant="secondary"
                disabled={isLoading || formProps.isSubmitting}
                onClick={backHandler}
                className="w-50"
              >
                Отменить
              </UDButton>
            </div>

            <PreventNavigationModal />
          </form>
        );
      }}
    </Formik>
  );
};

export default TeamForm;
